import type { ErrorApi } from '@epms/shared';

export class ErrorDeApi extends Error {
  status: number;
  codigo: string;
  detalles: { campo: string; mensaje: string }[];
  constructor(status: number, cuerpo: Partial<ErrorApi>) {
    super(cuerpo.mensaje ?? `Error ${status}`);
    this.status = status;
    this.codigo = cuerpo.error ?? 'error';
    this.detalles = cuerpo.detalles ?? [];
  }
}

/** Avisa a la app que la sesión venció (para volver al login). */
export const EVENTO_SESION_VENCIDA = 'epms:sesion-vencida';

async function pedir<T>(metodo: string, url: string, cuerpo?: unknown): Promise<T> {
  const formulario = cuerpo instanceof FormData;
  const r = await fetch(url, {
    method: metodo,
    credentials: 'same-origin',
    headers: {
      // Encabezado propio: la API rechaza los pedidos que cambian algo sin él (CSRF).
      'x-epms': '1',
      // Con FormData el navegador pone el content-type (multipart, con su separador).
      ...(cuerpo !== undefined && !formulario ? { 'content-type': 'application/json' } : {}),
    },
    body: formulario ? cuerpo : cuerpo !== undefined ? JSON.stringify(cuerpo) : undefined,
  });
  const texto = await r.text();
  const datos = texto ? JSON.parse(texto) : null;
  if (!r.ok) {
    if (r.status === 401 && !url.startsWith('/api/auth/')) window.dispatchEvent(new Event(EVENTO_SESION_VENCIDA));
    throw new ErrorDeApi(r.status, datos ?? {});
  }
  return datos as T;
}

export const api = {
  get: <T>(url: string) => pedir<T>('GET', url),
  post: <T>(url: string, cuerpo?: unknown) => pedir<T>('POST', url, cuerpo ?? {}),
  put: <T>(url: string, cuerpo: unknown) => pedir<T>('PUT', url, cuerpo),
  patch: <T>(url: string, cuerpo: unknown) => pedir<T>('PATCH', url, cuerpo),
  /** Sube archivos como multipart, todos en el campo "archivos". */
  subir: <T>(url: string, archivos: File[]) => {
    const f = new FormData();
    for (const a of archivos) f.append('archivos', a, a.name);
    return pedir<T>('POST', url, f);
  },
  del: <T>(url: string) => pedir<T>('DELETE', url),
};

/** POST que devuelve un archivo: lo baja con el nombre que manda el servidor. */
export async function descargar(url: string, cuerpo: unknown, nombrePorDefecto: string): Promise<void> {
  const r = await fetch(url, {
    method: 'POST', credentials: 'same-origin',
    headers: { 'x-epms': '1', 'content-type': 'application/json' },
    body: JSON.stringify(cuerpo),
  });
  if (!r.ok) {
    if (r.status === 401) window.dispatchEvent(new Event(EVENTO_SESION_VENCIDA));
    const texto = await r.text();
    throw new ErrorDeApi(r.status, texto ? JSON.parse(texto) : {});
  }
  const nombre = decodeURIComponent(r.headers.get('content-disposition')?.match(/filename\*=UTF-8''([^;]+)/)?.[1] ?? '') || nombrePorDefecto;
  const enlace = document.createElement('a');
  enlace.href = URL.createObjectURL(await r.blob());
  enlace.download = nombre;
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  setTimeout(() => URL.revokeObjectURL(enlace.href), 60_000);
}

export function mensajeDeError(e: unknown): string {
  return e instanceof Error ? e.message : 'Error inesperado';
}
